import type { CollaborationLocalChangeState } from "@workith/shared";
import { AlertTriangle, Check, CloudOff, RefreshCw } from "lucide-react";
import { collaborationLocalChangeText } from "../collaboration/collaborationEditorState";

interface DocumentStatusProps {
  localChanges: CollaborationLocalChangeState;
  /** When this tab last handed everything to the collaboration server. */
  lastSyncedAt?: Date;
}

const TONE: Readonly<Record<CollaborationLocalChangeState, string>> = {
  clean: "text-green-600 dark:text-green-400",
  syncing: "text-muted-foreground",
  pending: "text-amber-600 dark:text-amber-400",
  rejected: "text-destructive",
};

const ICON = {
  clean: Check,
  syncing: RefreshCw,
  pending: CloudOff,
  rejected: AlertTriangle,
} as const;

function formatSyncedAt(date: Date): string {
  const diffInSeconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "방금 전";
  }
  if (diffInSeconds < 3600) {
    return `${Math.floor(diffInSeconds / 60)}분 전`;
  }
  return date.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
}

/**
 * The local-change axis, as text.
 *
 * Wording stays on "전송" rather than "저장": the server persists on a debounce
 * and sends no write acknowledgement, so a synced client must not claim the
 * database has the content (ADR-022).
 */
export default function DocumentStatus({ localChanges, lastSyncedAt }: DocumentStatusProps) {
  const label = collaborationLocalChangeText(localChanges);
  const Icon = ICON[localChanges];
  const text =
    localChanges === "clean" && lastSyncedAt ? `${formatSyncedAt(lastSyncedAt)} 전송됨` : label;

  return (
    <div className="inline-flex items-center gap-2 text-sm leading-none">
      <Icon
        className={`h-4 w-4 shrink-0 ${TONE[localChanges]} ${localChanges === "syncing" ? "animate-spin" : ""}`}
        aria-hidden="true"
      />
      <span className={`${TONE[localChanges]} leading-none`}>{text}</span>
    </div>
  );
}
